import { useEffect } from 'react';
import { observer } from 'mobx-react-lite';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Button,
  Chip,
  Stack,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import PushPinIcon from '@mui/icons-material/PushPin';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import { useForumStore, useAuthStore } from '../../stores/root_store';
import { AuthorName } from '../../components/AuthorName';
import type { ForumPost } from '../../types';

function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / (1000 * 60));
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

  if (diffMins < 1) return 'just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString();
}

const PostItem = ({ post, sectionSlug }: { post: ForumPost; sectionSlug: string }) => {
  return (
    <Card
      component={Link}
      to={`/assistant/forum/${sectionSlug}/${post.slug}`}
      sx={{
        mb: 2,
        textDecoration: 'none',
        display: 'block',
        '&:hover': { bgcolor: 'action.hover' },
        borderLeft: post.sticky ? 4 : 0,
        borderColor: 'primary.main',
      }}
    >
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
              {post.sticky && (
                <PushPinIcon fontSize="small" color="primary" />
              )}
              <Typography variant="h6" component="span">
                {post.title}
              </Typography>
              {post.label && (
                <Chip
                  label={post.label}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              )}
            </Box>

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
              <Typography variant="body2" color="text.secondary" component="span">
                by <AuthorName name={post.author_name} userUuid={post.user_uuid} />
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {formatRelativeTime(post.date_created)}
              </Typography>
              {post.edited && (
                <Typography variant="caption" color="text.secondary">
                  (edited)
                </Typography>
              )}
            </Box>

            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
              }}
            >
              {post.body}
            </Typography>
          </Box>

          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 0.5,
              color: 'text.secondary',
            }}
          >
            <ChatBubbleOutlineIcon fontSize="small" />
            <Typography variant="body2">{post.comment_count}</Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export const ForumSectionPage = observer(() => {
  const { sectionSlug } = useParams<{ sectionSlug: string }>();
  const navigate = useNavigate();
  const forumStore = useForumStore();
  const authStore = useAuthStore();

  const section = sectionSlug ? forumStore.getSectionBySlug(sectionSlug) : null;
  const posts = sectionSlug ? forumStore.getPostsForSection(sectionSlug) : [];
  const hasMore = sectionSlug ? forumStore.hasMorePosts(sectionSlug) : false;

  // Fetch posts on mount or section change
  useEffect(() => {
    if (authStore.initialized && sectionSlug) {
      forumStore.fetchPosts(sectionSlug);
    }
  }, [authStore.initialized, sectionSlug, forumStore]);

  if (!sectionSlug) {
    return (
      <Alert severity="error">Section not found</Alert>
    );
  }

  if (forumStore.postsLoading && posts.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (forumStore.postsError) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {forumStore.postsError}
      </Alert>
    );
  }

  return (
    <Box>
      {/* Section Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          {section && (
            <>
              <Typography variant="h5">{section.title}</Typography>
              {section.description && (
                <Typography variant="body2" color="text.secondary">
                  {section.description}
                </Typography>
              )}
            </>
          )}
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate(`/assistant/forum/${sectionSlug}/new`)}
        >
          New Post
        </Button>
      </Box>

      {/* Posts List */}
      {posts.length === 0 ? (
        <Card>
          <CardContent>
            <Stack spacing={2} alignItems="center" sx={{ py: 4 }}>
              <ChatBubbleOutlineIcon sx={{ fontSize: 48, color: 'text.secondary' }} />
              <Typography color="text.secondary">
                No posts in this section yet. Be the first to post!
              </Typography>
              <Button
                variant="outlined"
                startIcon={<AddIcon />}
                onClick={() => navigate(`/assistant/forum/${sectionSlug}/new`)}
              >
                Create Post
              </Button>
            </Stack>
          </CardContent>
        </Card>
      ) : (
        <>
          {posts.map((post) => (
            <PostItem key={post.uuid} post={post} sectionSlug={sectionSlug} />
          ))}

          {hasMore && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Button
                onClick={() => forumStore.fetchPosts(sectionSlug, true)}
                disabled={forumStore.postsLoading}
              >
                {forumStore.postsLoading ? (
                  <CircularProgress size={20} />
                ) : (
                  'Load More'
                )}
              </Button>
            </Box>
          )}
        </>
      )}
    </Box>
  );
});
